import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import AppFooter from '../components/AppFooter';
import ProfessionalPitch from '../components/landing/ProfessionalPitch';
import { 
  Globe, 
  Shield, 
  Users, 
  Target, 
  Eye, 
  Zap,
  Scale,
  Building2,
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Handshake,
  Lightbulb,
  Lock
} from 'lucide-react';

const AboutPage = () => {
  const { language, toggleLanguage, localize } = useLanguage();
  const isAr = language === 'ar';

  const t = (ar, en) => localize({ ar, en });

  return (
    <div className={`min-h-screen bg-white ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#059669] to-[#7b2cbf] flex items-center justify-center shadow-lg shadow-emerald-200">
              <span className="text-white font-bold text-sm">LT</span>
            </div>
            <span className="font-bold text-slate-900 text-lg">Learn With Trust</span>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#059669] transition-colors"
              data-testid="about-language-toggle"
            >
              <Globe className="w-4 h-4" />
              {isAr ? 'English' : 'العربية'}
            </button>
            <Link 
              to="/" 
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#059669] transition-colors"
              data-testid="about-back-home"
            >
              {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              {t('الرئيسية', 'Home')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#059669]/[0.03] rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 pt-24 pb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-[#059669]/5 text-[#059669] text-sm font-medium px-4 py-2 rounded-full mb-8">
            <Building2 className="w-4 h-4" />
            {t('شركة ترست اي دي تكنو للتطوير والابتكار', 'Trust Ed Techno for Development & Innovation')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight" data-testid="about-hero-title">
            {t('نُعيد تعريف طريقة التواصل', 'Redefining How')}
            <br />
            <span className="bg-gradient-to-r from-[#059669] to-[#6ee7b7] bg-clip-text text-transparent">
              {t('بين الطلاب والأساتذة', 'Students Meet Tutors')}
            </span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed" data-testid="about-hero-subtitle">
            {t(
              'Learn With Trust ليست مؤسسة تعليمية. نحن منصة تقنية مبتكرة صُممت لتكون الجسر الموثوق بين الطلاب والأساتذة المستقلين في الإمارات العربية المتحدة.',
              'Learn With Trust is not an educational institution. We are an innovative technology platform designed to be the trusted bridge between students and independent tutors across the United Arab Emirates.'
            )}
          </p>
        </div>
      </section>

      {/* What We Are / What We Are Not */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-8">
          {/* What We Are */}
          <div className="relative group">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#059669]/20 to-emerald-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-white border border-slate-100 rounded-2xl p-8 h-full shadow-sm" data-testid="about-what-we-are">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                {t('ما نحن', 'What We Are')}
              </h2>
              <ul className="space-y-4">
                {[
                  t('منصة تقنية وسيطة تربط بين الطلاب والأساتذة المستقلين', 'A technology intermediary platform connecting students with independent tutors'),
                  t('نظام ذكي للبحث والمطابقة والحجز والدفع الآمن', 'An intelligent system for search, matching, booking, and secure payment'),
                  t('بيئة رقمية آمنة مع فصول افتراضية وأدوات تفاعلية', 'A secure digital environment with virtual classrooms and interactive tools'),
                  t('شريك تقني يحمي حقوق جميع الأطراف', 'A technology partner protecting the rights of all parties'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* What We Are Not */}
          <div className="relative group">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#059669]/20 to-rose-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-white border border-slate-100 rounded-2xl p-8 h-full shadow-sm" data-testid="about-what-we-are-not">
              <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center mb-6">
                <Scale className="w-6 h-6 text-rose-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                {t('ما لسنا عليه', 'What We Are Not')}
              </h2>
              <ul className="space-y-4">
                {[
                  t('لسنا مدرسة أو مؤسسة تعليمية أو مركز تدريب', 'We are not a school, educational institution, or training center'),
                  t('لا نقدم خدمات تعليمية بشكل مباشر', 'We do not provide educational services directly'),
                  t('لا نتحكم في محتوى الدروس أو أساليب التدريس', 'We do not control lesson content or teaching methods'),
                  t('المسؤولية التعليمية تقع بالكامل على الأستاذ المستقل', 'Educational responsibility lies entirely with the independent tutor'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-rose-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vision / Mission / Values */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              {t('رؤيتنا ورسالتنا', 'Our Vision & Mission')}
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-[#059669] to-[#6ee7b7] rounded-full mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Eye,
                color: 'emerald',
                title: t('الرؤية', 'Vision'),
                text: t(
                  'أن نكون المنصة التقنية الأولى في المنطقة لربط الطلاب بالأساتذة المستقلين، مع ضمان أعلى معايير الأمان والشفافية والجودة.',
                  'To be the leading technology platform in the region for connecting students with independent tutors, ensuring the highest standards of safety, transparency, and quality.'
                ),
              },
              {
                icon: Target,
                color: 'emerald',
                title: t('الرسالة', 'Mission'),
                text: t(
                  'تمكين الطلاب والأساتذة من التواصل بسهولة وأمان عبر أدوات تقنية متقدمة، مع توفير بيئة رقمية موثوقة تحمي حقوق جميع الأطراف.',
                  'Empowering students and tutors to connect seamlessly and securely through advanced technology tools, while providing a trusted digital environment that protects the rights of all parties.'
                ),
              },
              {
                icon: Lightbulb,
                color: 'amber',
                title: t('القيم', 'Values'),
                text: t(
                  'الشفافية في كل معاملة. الأمان في كل تفاعل. الابتكار في كل حل. الموثوقية في كل خطوة. نحترم استقلالية الأستاذ ونحمي خصوصية الطالب.',
                  'Transparency in every transaction. Security in every interaction. Innovation in every solution. Reliability in every step. We respect tutor independence and protect student privacy.'
                ),
              },
            ].map((card, i) => {
              const Icon = card.icon;
              const bgMap = { blue: 'bg-emerald-50', blue: 'bg-emerald-50', amber: 'bg-amber-50' };
              const iconMap = { blue: 'text-emerald-600', blue: 'text-emerald-600', amber: 'text-amber-600' };
              return (
                <div key={i} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-300" data-testid={`about-value-${i}`}>
                  <div className={`w-14 h-14 rounded-2xl ${bgMap[card.color]} flex items-center justify-center mb-6`}>
                    <Icon className={`w-7 h-7 ${iconMap[card.color]}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                  <p className="text-slate-500 leading-relaxed">{card.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Professional Pitch — Trust Creator Suite */}
      <ProfessionalPitch language={language} />

      {/* How It Works */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            {t('كيف تعمل المنصة', 'How the Platform Works')}
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto">
            {t(
              'نوفر التقنية والأدوات، والأستاذ يقدم العلم',
              'We provide the technology and tools; the tutor delivers the knowledge'
            )}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { step: '01', icon: Users, title: t('التسجيل', 'Register'), desc: t('ينشئ الطالب والأستاذ حساباتهم ويوثقون ملفاتهم الشخصية', 'Students and tutors create accounts and verify their profiles') },
            { step: '02', icon: Target, title: t('البحث والمطابقة', 'Search & Match'), desc: t('يبحث الطالب عن الأستاذ المناسب حسب المادة والموقع والتقييم', 'Students find the right tutor by subject, location, and rating') },
            { step: '03', icon: Handshake, title: t('الحجز والدفع', 'Book & Pay'), desc: t('يحجز الطالب الدرس ويدفع بأمان عبر المنصة', 'Students book lessons and pay securely through the platform') },
            { step: '04', icon: Globe, title: t('الدرس الافتراضي', 'Virtual Lesson'), desc: t('يلتقي الطالب والأستاذ في فصل افتراضي مزود بسبورة وأدوات تفاعلية', 'Student and tutor meet in a virtual classroom with whiteboard and interactive tools') },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="text-center group" data-testid={`about-step-${i}`}>
                <div className="relative mx-auto mb-5">
                  <div className="w-16 h-16 rounded-2xl bg-[#059669]/5 flex items-center justify-center mx-auto group-hover:bg-[#059669]/10 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-[#059669]" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#059669] text-white text-xs font-bold flex items-center justify-center shadow-lg">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="bg-gradient-to-br from-[#059669] to-[#7b2cbf] text-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold mb-3">
              {t('الأمان والثقة', 'Trust & Safety')}
            </h2>
            <p className="text-white/70 max-w-xl mx-auto">
              {t(
                'نلتزم بأعلى معايير الأمان والخصوصية لحماية جميع المستخدمين',
                'We adhere to the highest standards of security and privacy to protect all users'
              )}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: t('التحقق من الهوية', 'Identity Verification'), desc: t('يتم التحقق من هوية وشهادات جميع الأساتذة قبل اعتمادهم على المنصة', 'All tutors undergo identity and credential verification before platform approval') },
              { icon: Lock, title: t('مدفوعات آمنة', 'Secure Payments'), desc: t('جميع المعاملات المالية مشفرة ومحمية عبر بوابة دفع معتمدة', 'All financial transactions are encrypted and protected through a certified payment gateway') },
              { icon: Eye, title: t('المراقبة الصامتة', 'Stealth Monitoring'), desc: t('يستطيع ولي الأمر مراقبة الدرس في أي وقت دون علم الأستاذ أو الطالب', 'Parents can monitor lessons at any time without the tutor or student knowing') },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-colors duration-300" data-testid={`about-safety-${i}`}>
                  <Icon className="w-8 h-8 mb-4 text-white/90" />
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Child Protection */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-200" data-testid="about-child-protection">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              {t('حماية الأطفال بالذكاء الاصطناعي', 'AI-Powered Child Protection')}
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 leading-relaxed text-sm">
            <p>
              {t(
                'تلتزم منصة Learn With Trust بتوفير بيئة تعليمية آمنة تماماً. لذلك، تم تزويد المنصة بنظام حماية متقدم يعمل بالذكاء الاصطناعي يقوم بفحص جميع المراسلات بين الطلاب والمعلمين بشكل فوري وتلقائي.',
                'Learn With Trust is committed to providing a completely safe learning environment. The platform is equipped with an advanced AI-powered protection system that scans all communications between students and teachers instantly and automatically.'
              )}
            </p>
            <p>
              {t(
                'يرصد النظام أي إيذاء لفظي أو معنوي، أو محتوى غير لائق، أو محاولات تواصل مشبوهة، ويتم حظرها فوراً مع إخطار إدارة المنصة لاتخاذ الإجراءات اللازمة.',
                'The system detects any verbal or emotional abuse, inappropriate content, or suspicious communication attempts, blocking them immediately and notifying the platform administration to take necessary action.'
              )}
            </p>
            <p>
              {t(
                'يتم الفحص بشكل آلي بالكامل عبر الذكاء الاصطناعي دون تدخل بشري في قراءة الرسائل، مما يضمن الخصوصية مع الحماية القصوى.',
                'Scanning is fully automated via AI without any human intervention in reading messages, ensuring privacy alongside maximum protection.'
              )}
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-medium">
              <Lock className="w-3 h-3" />
              {t('فحص تلقائي بالذكاء الاصطناعي', 'Automated AI scanning')}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-medium">
              <Shield className="w-3 h-3" />
              {t('حظر فوري للمحتوى الضار', 'Instant harmful content blocking')}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-medium">
              <Eye className="w-3 h-3" />
              {t('خصوصية تامة', 'Complete privacy')}
            </span>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100" data-testid="about-legal-disclaimer">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <Scale className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              {t('إخلاء المسؤولية القانوني', 'Legal Disclaimer')}
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 leading-relaxed text-sm">
            <p>
              {t(
                'منصة Learn With Trust مملوكة ومُدارة من قبل شركة ترست اي دي تكنو للتطوير والابتكار ذ.م.م، وهي شركة مسجلة في إمارة أبوظبي، الإمارات العربية المتحدة.',
                'The Learn With Trust platform is owned and operated by Trust Ed Techno for Development and Innovation L.L.C., a company registered in the Emirate of Abu Dhabi, United Arab Emirates.'
              )}
            </p>
            <p>
              {t(
                'تعمل المنصة كوسيط تقني يربط بين الطلاب والأساتذة المستقلين.',
                'The platform operates as a technology intermediary connecting students with independent tutors.'
              )}
            </p>
            <p>
              {t(
                'تحتفظ المنصة بنسبة عمولة من كل معاملة مقابل توفير البنية التقنية وأنظمة الدفع الآمنة والفصول الافتراضية وخدمات الدعم. جميع الشروط والأحكام متاحة للمراجعة عبر صفحة الشروط والأحكام.',
                'The platform retains a commission from each transaction in exchange for providing the technical infrastructure, secure payment systems, virtual classrooms, and support services. All terms and conditions are available for review on the Terms & Conditions page.'
              )}
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/terms" className="text-sm text-[#059669] hover:underline font-medium">
              {t('الشروط والأحكام', 'Terms & Conditions')}
            </Link>
            <span className="text-slate-300">|</span>
            <Link to="/privacy" className="text-sm text-[#059669] hover:underline font-medium">
              {t('سياسة الخصوصية', 'Privacy Policy')}
            </Link>
            <span className="text-slate-300">|</span>
            <Link to="/contact" className="text-sm text-[#059669] hover:underline font-medium">
              {t('تواصل معنا', 'Contact Us')}
            </Link>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="border-t border-slate-100 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#059669] to-[#7b2cbf] flex items-center justify-center shadow-lg shadow-emerald-200">
                  <span className="text-white font-bold">LT</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">Learn With Trust</h3>
                  <p className="text-sm text-slate-500">{t('منصة تعلم مع ترست', 'Learn With Trust Platform')}</p>
                </div>
              </div>
              <p className="text-slate-500 leading-relaxed mb-6">
                {t(
                  'من إنتاج شركة ترست اي دي تكنو للتطوير والابتكار ذ.م.م. نسعى لتمكين التعليم الفردي من خلال التقنية، مع الحفاظ على استقلالية كل أستاذ وضمان أمان كل طالب.',
                  'A product of Trust Ed Techno for Development & Innovation L.L.C. We strive to empower individual education through technology, while preserving every tutor\'s independence and ensuring every student\'s safety.'
                )}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">{t('الشركة الأم', 'Parent Company')}</h4>
                <p className="text-slate-500">Trust Ed Techno</p>
                <p className="text-slate-400 text-xs mt-1">{t('ترست اي دي تكنو للتطوير والابتكار ذ.م.م', 'Trust Ed Techno for Development and Innovation L.L.C.')}</p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">{t('المقر', 'Headquarters')}</h4>
                <p className="text-slate-500">{t('أبوظبي، الإمارات العربية المتحدة', 'Abu Dhabi, United Arab Emirates')}</p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">{t('الموقع', 'Website')}</h4>
                <a href="https://www.trustedtechno.com" target="_blank" rel="noopener noreferrer" className="text-[#059669] hover:underline block">
                  www.TrustEdTechno.com
                </a>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">{t('البريد', 'Email')}</h4>
                <a href="mailto:info@trustedtechno.com" className="text-[#059669] hover:underline">
                  info@trustedtechno.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <AppFooter language={language} variant="full" />
    </div>
  );
};

export default AboutPage;
